import React, { FormEvent, useState, useEffect } from 'react';
import { Typography, makeStyles, Button, Theme } from '@material-ui/core';
import { useAppState } from '../../../state';
import logo from '../../../images/Fit_After_50_Logo.svg';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    width: '460px',
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: '28px',
    background: 'radial-gradient(120% 70% at 50% 0%, rgba(99, 102, 241, 0.08) 0%, rgba(6, 182, 212, 0.04) 45%, #ffffff 100%), #ffffff',
    boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.2)',
    padding: '38px 36px 34px 36px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    margin: 'auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    [theme.breakpoints.down('xs')]: {
      padding: '28px 20px 24px 20px',
      borderRadius: '22px',
    },
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '4px',
  },
  logo: {
    width: '135px',
    height: 'auto',
    maxHeight: '85px',
    objectFit: 'contain',
    display: 'block',
  },
  title: {
    fontSize: '32px',
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.5px',
    marginTop: '16px',
    marginBottom: '4px',
    lineHeight: 1.15,
    fontFamily: 'inherit',
    [theme.breakpoints.down('xs')]: {
      fontSize: '26px',
    },
  },
  subtitle: {
    fontSize: '16.5px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '28px',
    fontFamily: 'inherit',
    [theme.breakpoints.down('xs')]: {
      fontSize: '14.5px',
      marginBottom: '22px',
    },
  },
  form: {
    width: '100%',
  },
  infoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0 8px',
    marginBottom: '30px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      padding: '0 2px',
      marginBottom: '22px',
    },
  },
  infoCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    minWidth: 0,
  },
  infoLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
  },
  infoLabelText: {
    fontSize: '14.5px',
    fontWeight: 600,
    color: '#64748b',
    marginLeft: '6px',
  },
  infoValueName: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#0f172a',
    marginTop: '6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '190px',
    textAlign: 'left',
    fontFamily: 'inherit',
    [theme.breakpoints.down('xs')]: {
      fontSize: '17px',
      maxWidth: '135px',
    },
  },
  infoValueCode: {
    fontSize: '24px',
    fontWeight: 800,
    color: '#0f172a',
    marginTop: '4px',
    textAlign: 'left',
    fontFamily: 'inherit',
    [theme.breakpoints.down('xs')]: {
      fontSize: '20px',
    },
  },
  divider: {
    width: '1px',
    height: '50px',
    background: '#e2e8f0',
    margin: '0 20px',
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      margin: '0 10px',
      height: '42px',
    },
  },
  connectButton: {
    width: '100%',
    height: '54px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #6366f1 100%)',
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.35)',
    color: '#ffffff !important',
    textTransform: 'none',
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '0.3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    fontFamily: 'inherit',
    '&:hover': {
      background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 50%, #4f46e5 100%)',
      boxShadow: '0 10px 28px rgba(124, 58, 237, 0.45)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: '#94a3b8',
      color: '#ffffff !important',
      boxShadow: 'none',
      cursor: 'not-allowed',
      transform: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      height: '48px',
      fontSize: '16.5px',
    },
  },
}));

interface RoomNameScreenProps {
  name: string;
  roomName: string;
  setName: (name: string) => void;
  setRoomName: (roomName: string) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export default function RoomNameScreen({ name, roomName, setName, setRoomName, handleSubmit }: RoomNameScreenProps) {
  const classes = useStyles();
  const { user } = useAppState();

  // 1. Estado de bloqueo
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    // EL ESCÁNER: Revisa la memoria constantemente para atrapar el cambio de P2
    const checkStorage = () => {
      const ended = window.sessionStorage.getItem('meeting_ended') === 'true';

      if (ended && !isBlocked) {
        console.log('DEBUG [Lobby]: Reunión terminada detectada. Bloqueando...');
        setIsBlocked(true);
        window.sessionStorage.removeItem('meeting_ended');
      }
    };

    const interval = setInterval(checkStorage, 100);
    return () => clearInterval(interval);
  }, [isBlocked]);

  const isTrainer =
    window.location.search.includes('entrenador=true') ||
    window.location.search.includes('trainer=true') ||
    !window.location.search.includes('client=true');

  const displayName = name || user?.displayName || '---';

  return (
    <div className={classes.card}>
      {/* Top Logo */}
      <div className={classes.logoContainer}>
        <img src={logo} alt="Walba" className={classes.logo} />
      </div>

      {/* Title & Subtitle */}
      <Typography component="h1" className={classes.title}>
        Live Session
      </Typography>
      <Typography component="p" className={classes.subtitle}>
        {isTrainer ? 'Connect with your client.' : 'Connect with your trainer.'}
      </Typography>

      {/* Main Form */}
      <form onSubmit={handleSubmit} className={classes.form}>
        <div className={classes.infoContainer}>
          {/* Left Column: Trainer / Participant */}
          <div className={classes.infoCol}>
            <div className={classes.infoLabelRow}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span className={classes.infoLabelText}>
                {isTrainer ? 'Trainer' : 'Participant'}
              </span>
            </div>
            <div className={classes.infoValueName} title={displayName}>
              {displayName}
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={classes.divider} />

          {/* Right Column: Room Code */}
          <div className={classes.infoCol}>
            <div className={classes.infoLabelRow}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 7l-7 5 7 5V7z"></path>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              <span className={classes.infoLabelText}>Room Code</span>
            </div>
            <div className={classes.infoValueCode}>
              {roomName || '---'}
            </div>
          </div>
        </div>

        {/* Action Button: Connect */}
        <Button
          variant="contained"
          type="submit"
          disabled={(!name && !user?.displayName) || !roomName || isBlocked}
          className={classes.connectButton}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M23 7l-7 5 7 5V7z"></path>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
          </svg>
          <span>{isBlocked ? 'Meeting Ended' : 'Connect'}</span>
        </Button>
      </form>
    </div>
  );
}
